import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTabsModule } from '@angular/material/tabs';

import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltip } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from '@angular/router';
import { MY_DATE_FORMATS } from '../../../domain/registrator';
import { EventService } from '../../../services/event.service';
import { EventFreeRegistrationsComponent } from '../event-free-registrations/event-free-registrations.component';
import { EventPreferencesComponent } from '../event-preferences/event-preferences.component';
import { EventPromocodesComponent } from "../event-promocodes/event-promocodes.component";
import { EventRegistrationsComponent } from '../event-registrations/event-registrations.component';
import { AuthnService } from '../../../services/authn.service';
import { MessageService } from '../../../services/message.service';


@Component({
  selector: 'app-event-detail',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatRadioModule,
    MatCardModule,
    MatListModule,
    MatCheckboxModule,
    MatTabsModule,
    MatDatepickerModule,
    MatLuxonDateModule,
    MatSortModule,
    EventRegistrationsComponent,
    EventFreeRegistrationsComponent,
    EventPromocodesComponent,
    EventPreferencesComponent,
    MatTooltip
  ],
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_DATE_FORMATS,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB', // Or your desired locale
    },
  ],
  templateUrl: './event-detail.component.html',
  styleUrl: './event-detail.component.scss'
})
export class EventDetailComponent implements OnInit, OnDestroy {
  private readonly eventService = inject(EventService);
  private readonly authnService = inject(AuthnService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  event = this.route.parent!.snapshot.data['event'];

  constructor() { }

  ngOnInit() {
    // the resolver data is a snapshot taken when the :eventname route was
    // entered; re-fetch the event so values edited in the edit component
    // are reflected when navigating back
    const eventShortName = this.route.parent!.snapshot.paramMap.get('eventname');
    if (eventShortName) {
      this.eventService.getEventByName(eventShortName).subscribe(event => {
        if (event) {
          this.event = event;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.eventService.deleteCache();
    console.debug('destroying registrationList');
  }

  editEvent() {
    this.router.navigate(['edit'], { relativeTo: this.route });
  }

  generateParticipantsList() {
    console.debug('GenerateParticipantsList');
    if (this.event === null) {
      console.error('Event is null, cannot generate participants list');
      this.messageService.showMessage('Error: Event details not loaded.');
      return;
    }
    this.eventService.getParticipantsList(this.event.shortName).subscribe(response => {
      const contentDisposition = response.headers.get('Content-Disposition');
      console.debug(response.headers);
      let filename = `${this.event.shortName}_participants.xlsx`;
      console.debug("contentDisposition", contentDisposition);
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.body!], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    });
  }

  get isAdmin() {
    return (this.authnService.isAuthenticated && (this.authnService.roles!.includes('ROLE_ADMIN')));
  }

  copy(value: string | null | undefined) {
    navigator.clipboard.writeText(value ?? "N/A");
  }
}
